// Read-only "Recipes" tab body for the Inventory panel's left-column
// tabbed section. Renders inline (no SectionCard wrapper) so the
// ManufacturingTabbedSection chrome owns the card border / padding /
// title row. Was `RecipeInventorySection` pre-merge polish (which
// rendered its own SectionCard); the per-tab Refresh button moved to
// the parent's tab-rightSlot so it sits next to the segmented control.
import { useMemo, useState } from "react";
import type { MfgRecipeInventoryRow } from "@/lib/manufacturing/types";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

type Props = {
  rows: ReadonlyArray<MfgRecipeInventoryRow>;
  totalInventoryValue: number;
  loading: boolean;
  error: string | null;
};

export function RecipeInventoryBody({ rows, totalInventoryValue, loading, error }: Props) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      [r.recipe_name, r.recipe_code].join(" ").toLowerCase().includes(q)
    );
  }, [rows, search]);

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
        {error}
      </div>
    );
  }

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-zinc-400">Loading recipes…</div>;
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-400">
        No recipes counted yet. Switch to the <strong>Recipe count</strong> tab on the right and post an
        opening-balance count against a recipe (e.g. <em>40 trusses @ $87.50 each</em>) to start tracking.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <input
        className={inputClass}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search recipes by code or name…"
      />
      <div className="grid min-w-0 grid-cols-2 gap-3">
        <SnapshotTile label="Tracked recipes" value={String(rows.length)} accent="indigo" />
        <SnapshotTile
          label="Inventory value"
          value={totalInventoryValue.toLocaleString(undefined, { style: "currency", currency: "USD" })}
          accent="emerald"
        />
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
        {filtered.map((r) => (
          <RecipeInventoryRow key={r.id} row={r} />
        ))}
      </ul>
      {filtered.length === 0 ? (
        <div className="text-xs text-gray-500 dark:text-zinc-400">No recipes match your search.</div>
      ) : null}
    </div>
  );
}

function SnapshotTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: "indigo" | "emerald";
}) {
  const accentClass =
    accent === "indigo"
      ? "text-indigo-700 dark:text-indigo-300"
      : "text-emerald-700 dark:text-emerald-300";
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/50">
      <div className="text-xs text-gray-600 dark:text-zinc-300">{label}</div>
      <div className={`mt-1 break-all text-lg font-bold tabular-nums leading-tight sm:text-2xl ${accentClass}`}>
        {value}
      </div>
    </div>
  );
}

function RecipeInventoryRow({ row }: { row: MfgRecipeInventoryRow }) {
  return (
    <li className="flex items-center justify-between gap-2 py-2.5">
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">{row.recipe_name}</div>
        <div className="text-xs text-gray-600 dark:text-zinc-400">
          {row.recipe_code}
          {row.recipe_is_active ? null : " · inactive recipe"}
        </div>
        <div className="text-xs text-gray-600 dark:text-zinc-400">
          On hand: {Number(row.on_hand_qty).toFixed(1)} · WAC: ${Number(row.unit_cost).toFixed(2)}
        </div>
        {row.last_counted_at ? (
          <div className="text-[11px] text-gray-500 dark:text-zinc-500">
            Last counted: {new Date(row.last_counted_at).toLocaleString()}
          </div>
        ) : null}
      </div>
    </li>
  );
}
